/** @jsx jsx */
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import moment from 'moment'
import { useTranslate } from '@chordcommerce/gatsby-theme-autonomy'
import { Box, Button, Flex, Grid, jsx, Spinner, Text } from 'theme-ui'
import AgeVerification from '~/components/Checkout/Button/AgeVerification'
import FormInput from '~/components/Generic/Form/Input'
import useUiTextQuery from '~/hooks/graphql/queries/use-ui-text'
import {
  detectTransactionalNumber,
  storeTransactionalNumber,
  subscribeTransactionalNumber,
} from '~/services/attentive/client'

const AttentiveOptInForm = ({
  containerBg,
  defaultEmailAddress,
  defaultOptInValue,
  emailAddress,
  includeAgeVerification,
  includeEmailAddress,
}) => {
  const [apiError, setApiError] = useState(null)
  const [buttonIsLoading, setButtonIsLoading] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const translate = useTranslate()

  const form = useForm()
  const { getValues, handleSubmit, watch } = form
  const isAgeVerified = watch('ageVerification', true)
  const isOptingIn = watch('smsOptin', false)

  const onSubmit = async (data) => {
    if (buttonIsLoading || !isAgeVerified) return

    setButtonIsLoading(true)
    setApiError(null)

    try {
      await storeTransactionalNumber(getValues('phoneNumber'))
      await subscribeTransactionalNumber(
        emailAddress || getValues('emailAddress')
      )
      setHasSubmitted(true)
    } catch (error) {
      setApiError(translate('error.api.default'))
      console.error(error, { source: 'AttentiveOptInForm' })
    }

    setButtonIsLoading(false)
  }

  if (detectTransactionalNumber() && !hasSubmitted) {
    setHasSubmitted(true)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <AttentiveOptInContainer bg={containerBg}>
        {hasSubmitted && (
          <Text variant="body2" as="p">
            {translate('sms.confirmation')}
          </Text>
        )}
        {!hasSubmitted && (
          <Box>
            <AttentiveOptInFields
              defaultEmailAddress={defaultEmailAddress}
              defaultOptInValue={defaultOptInValue}
              emailAddress={emailAddress}
              includeAgeVerification={includeAgeVerification}
              includeEmailAddress={includeEmailAddress}
              parentForm={form}
            />
            <Box
              mt={2}
              px="26px"
              sx={{
                display: isOptingIn || includeEmailAddress ? 'block' : 'none',
              }}
            >
              <AttentiveOptInConfirm
                disabled={!isOptingIn}
                error={
                  !includeEmailAddress &&
                  !isAgeVerified &&
                  translate('checkout.age_validation')
                }
                isLoading={buttonIsLoading}
              />
              {apiError && (
                <Text variant="error" as="p" mt={2} mb={0}>
                  {apiError}
                </Text>
              )}
            </Box>
          </Box>
        )}
      </AttentiveOptInContainer>
    </form>
  )
}

const AttentiveOptInContainer = ({ bg, children }) => {
  return (
    <Box
      bg={bg || 'backgroundLight'}
      px={[2, 3]}
      py={2}
      sx={{ borderRadius: '10px' }}
    >
      {children}
    </Box>
  )
}

const AttentiveOptInFields = ({
  defaultEmailAddress,
  defaultOptInValue,
  includeAgeVerification,
  includeEmailAddress,
  parentForm,
}) => {
  const { errors, register, setValue, watch } = parentForm
  const translate = useTranslate()
  const smsUiText = useUiTextQuery('global', 'sms')

  const isOptingIn = watch('smsOptin', false)
  const birthDate = watch('birthDate')

  const isValidBirthDate = useCallback(
    (value) => {
      if (!isOptingIn) return true

      const age = moment().diff(moment(value, 'YYYY-MM-DD'), 'years')
      return age >= 21 && age <= 120
    },
    [isOptingIn]
  )

  useEffect(() => {
    setValue('ageVerification', !isOptingIn)
  }, [isOptingIn, setValue])

  useEffect(() => {
    if (!isOptingIn) return

    setValue('ageVerification', isValidBirthDate(birthDate))
  }, [birthDate, isOptingIn, isValidBirthDate, setValue])

  return (
    <Box>
      <Box
        mb={1}
        sx={{
          display: !isOptingIn && includeAgeVerification ? 'block' : 'none',
        }}
      >
        <AgeVerification parentForm={parentForm} />
      </Box>

      <Flex>
        <input
          type="checkbox"
          defaultChecked={defaultOptInValue}
          name="smsOptin"
          id="smsOptin"
          ref={register()}
        />
        <Text
          as="label"
          variant="body3"
          htmlFor="smsOptin"
          ml={1}
          mt="-2px"
          aria-label={`${
            smsUiText?.optin || translate('sms.optin')
          } Requires phone number and birth date when checked.`}
        >
          {smsUiText.optin || translate('sms.optin')}
        </Text>
      </Flex>
      <Box
        px="28px"
        sx={{ display: isOptingIn || includeEmailAddress ? 'grid' : 'none' }}
      >
        {includeEmailAddress && (
          <Box mt={1}>
            <FormInput
              name="emailAddress"
              title={'Email address'}
              label={'Email address'}
              aria-label={'Email address'}
              type="email"
              defaultValue={defaultEmailAddress}
              error={errors?.emailAddress?.message}
              ref={register({
                required: {
                  value: true,
                  message: translate('validation.required_field'),
                },
                pattern: {
                  value: /^\S+@\S+$/i,
                  message: translate('validation.invalid_email'),
                },
              })}
            />
          </Box>
        )}
        <Grid columns={[1, 2]} mt={1} sx={{ rowGap: 1 }}>
          <Box>
            <FormInput
              name="phoneNumber"
              title={'Phone number'}
              label={'Phone number'}
              aria-label={'Phone number'}
              type="tel"
              error={errors?.phoneNumber?.message}
              ref={register({
                required: {
                  value: isOptingIn,
                  message: translate('validation.required_field'),
                },
                pattern: {
                  value:
                    /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im,
                  message: translate('validation.invalid_phone'),
                },
              })}
            />
          </Box>
          <Box>
            <FormInput
              name="birthDate"
              title={'Birth date'}
              label={'Birth date'}
              aria-label={'Birth date'}
              type="date"
              error={errors?.birthDate?.message}
              ref={register({
                required: {
                  value: isOptingIn,
                  message: translate('validation.required_field'),
                },
                validate: (value) =>
                  isValidBirthDate(value) || translate('sms.age_validation'),
              })}
            />
          </Box>
        </Grid>
      </Box>
    </Box>
  )
}

const AttentiveOptInConfirm = ({ disabled, error, isLoading }) => {
  return (
    <Button type="submit" disabled={!!error || disabled} sx={{ width: '100%' }}>
      {isLoading && (
        <Spinner
          data-testid="spinner"
          size="15"
          color="inherit"
          sx={{
            '@media (prefers-reduced-motion)': {
              circle: {
                animationIterationCount: 8,
              },
            },
          }}
        />
      )}
      {!isLoading && (
        <Text>
          {!error && 'Opt in'}
          {error}
        </Text>
      )}
    </Button>
  )
}

const AttentiveOptIn = ({
  containerBg,
  defaultEmailAddress,
  defaultOptInValue = false,
  emailAddress,
  includeAgeVerification,
  includeEmailAddress,
  parentForm,
}) => {
  if (parentForm) {
    return (
      <AttentiveOptInContainer bg={containerBg}>
        <AttentiveOptInFields
          defaultEmailAddress={defaultEmailAddress}
          defaultOptInValue={defaultOptInValue}
          emailAddress={emailAddress}
          includeAgeVerification={includeAgeVerification}
          includeEmailAddress={includeEmailAddress}
          parentForm={parentForm}
        />
      </AttentiveOptInContainer>
    )
  }

  return (
    <AttentiveOptInForm
      containerBg={containerBg}
      defaultEmailAddress={defaultEmailAddress}
      defaultOptInValue={defaultOptInValue}
      emailAddress={emailAddress}
      includeAgeVerification={includeAgeVerification}
      includeEmailAddress={includeEmailAddress}
    />
  )
}

export default AttentiveOptIn
